import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { SectionSidebarNav, type SectionSidebarNavButtonItem } from "@/components/SectionSidebarNav";
import { Spinner } from "@/components/ui/Spinner";
import { getUserLocalLogin, useCurrentUser } from "@/lib/auth";
import { useRealtimeSync } from "@/lib/useRealtime";
import {
  userCanApproveZeiterfassung,
  userCanManageAllAbsences,
  userCanManageMitarbeiter,
  userCanManageZeiterfassungEntries,
  userCanManageZeiterfassungSettings,
} from "@/lib/zeiterfassungPermissions";
import { AbsenceOfficePanel } from "@/pages/admin/zeiterfassung/AbsenceOfficePanel";
import { ApprovalsPanel } from "@/pages/admin/zeiterfassung/ApprovalsPanel";
import { EntryCapturePanel } from "@/pages/admin/zeiterfassung/EntryCapturePanel";
import { EntriesOverview } from "@/pages/admin/zeiterfassung/EntriesOverview";
import { MonthOverviewPanel } from "@/pages/admin/zeiterfassung/MonthOverviewPanel";
import { YearOverviewPanel } from "@/pages/admin/zeiterfassung/YearOverviewPanel";
import { WorkTimeTablePanel } from "@/pages/admin/zeiterfassung/WorkTimeTablePanel";
import { AbsenceRequestPanel } from "@/pages/admin/zeiterfassung/AbsenceRequestPanel";
import { AccountsPanel } from "@/pages/admin/zeiterfassung/AccountsPanel";
import { usePersonalNavigation } from "@/lib/personalNav";
import { useRegisterPersonalSubNav } from "@/pages/personal/PersonalSubNavContext";
import "@/pages/admin/zeiterfassung/zeiterfassungMobile.css";

type ZeiterfassungTab =
  | "capture"
  | "overview"
  | "month"
  | "year"
  | "worktimes"
  | "absence"
  | "accounts"
  | "approvals"
  | "office";

export function ZeiterfassungIndexPage() {
  const { user, loading: userLoading } = useCurrentUser();
  const canManageEntries = userCanManageZeiterfassungEntries(user);
  const canManageSettings = userCanManageZeiterfassungSettings(user);
  const canApprove = userCanApproveZeiterfassung(user);
  const canManageAllAbsences = userCanManageAllAbsences(user);
  const canManageMitarbeiter = userCanManageMitarbeiter(user);
  const { backLink: personalBack, showHub: personalShowHub } = usePersonalNavigation();

  const [activeTab, setActiveTab] = useState<ZeiterfassungTab>("capture");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!userLoading && canApprove && !canManageEntries) {
      setActiveTab("approvals");
    }
  }, [userLoading, canApprove, canManageEntries]);

  const defaultEmployee = useMemo(() => {
    const login = getUserLocalLogin(user);
    const display = user?.name || user?.displayName || login;
    return { login, display };
  }, [user]);

  const bumpRefresh = () => setRefreshKey((k) => k + 1);

  const isInputTab = activeTab === "capture" || activeTab === "absence";
  useRealtimeSync({
    domains: ["employee-times"],
    paused: isInputTab || activeTab === "office",
    onChange: bumpRefresh,
  });

  const navItems = useMemo((): SectionSidebarNavButtonItem[] => {
    const items: SectionSidebarNavButtonItem[] = [
      {
        key: "capture",
        label: "Erfassen",
        active: activeTab === "capture",
        onClick: () => setActiveTab("capture"),
      },
      {
        key: "absence",
        label: "Abwesenheit",
        active: activeTab === "absence",
        onClick: () => setActiveTab("absence"),
      },
      {
        key: "month",
        label: "Monat",
        active: activeTab === "month",
        onClick: () => setActiveTab("month"),
      },
      {
        key: "year",
        label: "Jahr",
        active: activeTab === "year",
        onClick: () => setActiveTab("year"),
      },
      {
        key: "worktimes",
        label: "Arbeitszeiten",
        active: activeTab === "worktimes",
        onClick: () => setActiveTab("worktimes"),
      },
    ];
    if (canManageEntries) {
      items.splice(1, 0, {
        key: "overview",
        label: "Übersicht",
        active: activeTab === "overview",
        onClick: () => setActiveTab("overview"),
      });
      items.push({
        key: "accounts",
        label: "Stundenkonten",
        active: activeTab === "accounts",
        onClick: () => setActiveTab("accounts"),
      });
    }
    if (canApprove) {
      items.push({
        key: "approvals",
        label: "Freigaben & Verstöße",
        active: activeTab === "approvals",
        onClick: () => setActiveTab("approvals"),
      });
    }
    if (canManageAllAbsences) {
      items.push({
        key: "office",
        label: "Abwesenheiten (Büro)",
        active: activeTab === "office",
        onClick: () => setActiveTab("office"),
      });
    }
    return items;
  }, [activeTab, canManageEntries, canApprove, canManageAllAbsences]);
  const { nestInParentNav } = useRegisterPersonalSubNav("zeiterfassung", navItems);

  const titleByTab: Record<ZeiterfassungTab, string> = {
    capture: "Zeiten erfassen",
    overview: "Übersicht",
    absence: "Abwesenheit",
    month: "Monatsübersicht",
    year: "Jahresübersicht",
    worktimes: "Arbeitszeiten",
    accounts: "Stundenkonten",
    approvals: "Freigaben & Verstöße",
    office: "Abwesenheiten (Büro)",
  };

  if (userLoading) {
    return (
      <AppShell title="Zeiterfassung" subtitle="Lade Berechtigungen…">
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Zeiterfassung"
      className="zeiterfassung-mobile-shell"
      subtitle={titleByTab[activeTab]}
      back={
        personalBack ??
        (canManageEntries && !personalShowHub
          ? { to: "/admin", label: "Administration" }
          : personalShowHub
            ? undefined
            : { to: "/", label: "Home" })
      }
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {canManageMitarbeiter && personalShowHub && (
            <Link
              to="/admin/mitarbeiter"
              className="rounded-full border border-separator px-3 py-1.5 text-sm font-medium text-label transition hover:border-accent hover:text-accent"
            >
              Mitarbeiter
            </Link>
          )}
          {canManageSettings && (
            <Link
              to="/zeiterfassung/einstellungen"
              className="rounded-full border border-separator px-3 py-1.5 text-sm font-medium text-label transition hover:border-accent hover:text-accent"
            >
              Einstellungen
            </Link>
          )}
        </div>
      }
    >
      {nestInParentNav || navItems.length <= 1 ? (
        <ZeiterfassungPanelContent
          activeTab={activeTab}
          defaultEmployee={defaultEmployee}
          refreshKey={refreshKey}
          onRefresh={bumpRefresh}
          canManageEntries={canManageEntries}
          canApprove={canApprove}
          canManageAllAbsences={canManageAllAbsences}
        />
      ) : (
        <div className="eh-bestellwesen-page-shell">
          <SectionSidebarNav items={navItems} ariaLabel="Zeiterfassung Bereiche" />
          <div className="eh-project-page-main min-w-0">
            <ZeiterfassungPanelContent
              activeTab={activeTab}
              defaultEmployee={defaultEmployee}
              refreshKey={refreshKey}
              onRefresh={bumpRefresh}
              canManageEntries={canManageEntries}
              canApprove={canApprove}
              canManageAllAbsences={canManageAllAbsences}
            />
          </div>
        </div>
      )}
    </AppShell>
  );
}

function ZeiterfassungPanelContent({
  activeTab,
  defaultEmployee,
  refreshKey,
  onRefresh,
  canManageEntries,
  canApprove,
  canManageAllAbsences,
}: {
  activeTab: ZeiterfassungTab;
  defaultEmployee: { login: string; display: string };
  refreshKey: number;
  onRefresh: () => void;
  canManageEntries: boolean;
  canApprove: boolean;
  canManageAllAbsences: boolean;
}) {
  return (
    <>
      {activeTab === "capture" && (
        <EntryCapturePanel
          defaultEmployee={defaultEmployee}
          onCreated={onRefresh}
          allowEmployeePicker={canManageEntries}
        />
      )}
      {activeTab === "overview" && canManageEntries && (
        <EntriesOverview refreshKey={refreshKey} onChanged={onRefresh} />
      )}
      {activeTab === "absence" && (
        <AbsenceRequestPanel
          refreshKey={refreshKey}
          defaultEmployeeLogin={defaultEmployee.login}
          defaultEmployeeDisplay={defaultEmployee.display}
          allowEmployeePicker={canManageEntries || canManageAllAbsences}
          allowOfficeDelete={canManageAllAbsences}
          includeHeroOverlay
          onChanged={onRefresh}
        />
      )}
      {activeTab === "month" && (
        <MonthOverviewPanel
          refreshKey={refreshKey}
          defaultEmployeeLogin={defaultEmployee.login}
          defaultEmployeeDisplay={defaultEmployee.display}
          allowEmployeePicker={canManageEntries || canManageAllAbsences}
          includeHeroOverlay
        />
      )}
      {activeTab === "year" && (
        <YearOverviewPanel
          refreshKey={refreshKey}
          defaultEmployeeLogin={defaultEmployee.login}
          defaultEmployeeDisplay={defaultEmployee.display}
          allowEmployeePicker={canManageEntries || canManageAllAbsences}
          includeHeroOverlay
        />
      )}
      {activeTab === "worktimes" && (
        <WorkTimeTablePanel
          refreshKey={refreshKey}
          defaultEmployeeLogin={defaultEmployee.login}
          defaultEmployeeDisplay={defaultEmployee.display}
          allowEmployeePicker={canManageEntries || canManageAllAbsences}
          includeHeroOverlay
        />
      )}
      {activeTab === "accounts" && canManageEntries && (
        <AccountsPanel refreshKey={refreshKey} />
      )}
      {activeTab === "approvals" && canApprove && (
        <ApprovalsPanel refreshKey={refreshKey} onChanged={onRefresh} />
      )}
      {activeTab === "office" && canManageAllAbsences && (
        <AbsenceOfficePanel refreshKey={refreshKey} onChanged={onRefresh} />
      )}
    </>
  );
}
